import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import "./SkillsMatrixComparison.css";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { getVerticalStacks, getSkillMatrixAiData } from "./api";
import { useSearchParams } from "react-router-dom";
import { getSharedSelection } from "./sharedSelection";
import { readStackSelection, stackSearchParams } from "./urlParams";

/**
 * SkillsMatrixComparison
 *
 * React port of the Angular skillset-matrix.component.ts/.html. Same rules
 * and logic as the source, only look & feel changed.
 *
 * Selector: THREE cascading multiselects - Vertical Stack (max 3), Sub
 * Stack (no cap), Job Title (max 5) - confirmed in the source .html. This
 * is a DIFFERENT field set from JobSpecComparison's single-select cascade;
 * the two pages do not share selector logic.
 *
 * Data fetch is server-side by vertical_stack_id only; Sub Stack and Job
 * Title filtering happen CLIENT-SIDE here after fetch, exactly matching
 * the real Angular source's onSubStackChange/onJobTitleChange/applyFilters
 * (a confirmed, preserved characteristic - not a bug).
 */
export default function SkillsMatrixComparison() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSelectionRef = useRef(readStackSelection(searchParams, getSharedSelection()));
  const pendingSubStackRef = useRef(initialSelectionRef.current.subStackId);
  const [verticalStacks, setVerticalStacks] = useState([]);
  const [skillMatrixData, setSkillMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const [selectedVerticalStackIds, setSelectedVerticalStackIds] = useState([]);
  const [selectedSubStackIds, setSelectedSubStackIds] = useState([]);
  const [selectedJobTitleIds, setSelectedJobTitleIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getVerticalStacks()
      .then((rows) => {
        if (cancelled) return;
        const verticals = (rows || []).filter((v) => v.is_active);
        setVerticalStacks(verticals);
        const vertical = verticals.find((v) => v.id === initialSelectionRef.current.verticalStackId);
        if (vertical) setSelectedVerticalStackIds([vertical.id]);
      })
      .catch(() => !cancelled && setVerticalStacks([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // --- fetch, mirrors loadSkillMatrixData() ---
  useEffect(() => {
    if (!selectedVerticalStackIds.length) {
      setSkillMatrixData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSkillMatrixAiData(selectedVerticalStackIds)
      .then((rows) => {
        if (cancelled) return;
        setSkillMatrixData(rows || []);
        if (pendingSubStackRef.current != null) {
          const subId = pendingSubStackRef.current;
          pendingSubStackRef.current = null;
          const match = (rows || []).some((item) => item.sub_stack_id === subId);
          if (match) setSelectedSubStackIds([subId]);
        }
      })
      .catch(() => !cancelled && setSkillMatrixData([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedVerticalStackIds]);

  const handleVerticalStackChange = useCallback((ids) => {
    pendingSubStackRef.current = null;
    setSelectedVerticalStackIds(ids);
    setSelectedSubStackIds([]);
    setSelectedJobTitleIds([]);
    // The shared URL carries the first selection from each multiselect.
    setSearchParams(stackSearchParams(ids[0]), { replace: true });
  }, [setSearchParams]);

  const handleSubStackChange = useCallback((ids) => {
    pendingSubStackRef.current = null;
    setSelectedSubStackIds(ids);
    setSelectedJobTitleIds([]);
    setSearchParams(stackSearchParams(selectedVerticalStackIds[0], ids[0]), { replace: true });
  }, [selectedVerticalStackIds, setSearchParams]);

  // --- client-side derived option lists, mirrors updateAvailableOptions() / onSubStackChange() ---

  const availableSubStacks = useMemo(() => {
    const verticalStackNameById = new Map(verticalStacks.map((v) => [v.id, v.name]));
    const map = new Map();
    skillMatrixData.forEach((item) => {
      if (!map.has(item.sub_stack_id)) {
        const vsName = verticalStackNameById.get(item.vertical_stack_id) || "Unknown";
        map.set(item.sub_stack_id, {
          id: item.sub_stack_id,
          name: `${item.sub_stack_name} (${vsName})`,
          originalName: item.sub_stack_name,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.originalName.localeCompare(b.originalName));
  }, [skillMatrixData, verticalStacks]);

  const availableJobTitles = useMemo(() => {
    if (!selectedSubStackIds.length) return [];
    const filtered = skillMatrixData
      .filter((item) => selectedSubStackIds.includes(item.sub_stack_id))
      .map((item) => ({
        id: item.job_title_id,
        name: item.job_title_name,
        subStackId: item.sub_stack_id,
        jobLevel: item.minerva_job_level,
      }));
    const unique = Array.from(new Map(filtered.map((jt) => [jt.id, jt])).values());
    unique.sort((a, b) => (a.subStackId !== b.subStackId ? a.subStackId - b.subStackId : (a.jobLevel || 0) - (b.jobLevel || 0)));
    return unique;
  }, [skillMatrixData, selectedSubStackIds]);

  // --- ported verbatim from transformData() ---
  const matrixData = useMemo(() => {
    let filtered = skillMatrixData;
    if (selectedSubStackIds.length) {
      filtered = filtered.filter((item) => selectedSubStackIds.includes(item.sub_stack_id));
    }
    if (selectedJobTitleIds.length) {
      filtered = filtered.filter((item) => selectedJobTitleIds.includes(item.job_title_id));
    }
    if (!filtered.length) return null;

    const jobTitlesMap = new Map();
    const categoriesMap = new Map();
    const skillMap = new Map();

    filtered.forEach((item) => {
      if (!jobTitlesMap.has(item.minerva_job_code)) {
        jobTitlesMap.set(item.minerva_job_code, {
          minervaJobCode: item.minerva_job_code,
          jobTitleName: item.job_title_name,
          minervaJobLevel: item.minerva_job_level,
          yearOnExperience: item.year_on_experience,
          subStackName: item.sub_stack_name,
        });
      }
      if (!categoriesMap.has(item.category_type)) categoriesMap.set(item.category_type, new Map());
      const skillsMap = categoriesMap.get(item.category_type);
      if (!skillsMap.has(item.category_name)) {
        skillsMap.set(item.category_name, { categoryName: item.category_name, categoryDescription: item.category_description });
      }
      skillMap.set(`${item.category_name}|${item.minerva_job_code}`, item.skill_level);
    });

    const jobTitles = Array.from(jobTitlesMap.values()).sort((a, b) =>
      a.subStackName !== b.subStackName ? a.subStackName.localeCompare(b.subStackName) : a.minervaJobLevel - b.minervaJobLevel
    );

    const allCategories = Array.from(categoriesMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([categoryType, skillsMap]) => ({ categoryType, skills: Array.from(skillsMap.values()) }));

    const filteredCategories = allCategories
      .map((category) => ({
        categoryType: category.categoryType,
        skills: category.skills.filter((skill) =>
          jobTitles.some((job) => {
            const level = skillMap.get(`${skill.categoryName}|${job.minervaJobCode}`);
            return level && level.trim() !== "";
          })
        ),
      }))
      .filter((category) => category.skills.length > 0);

    return { jobTitles, categories: filteredCategories, skillMap };
  }, [skillMatrixData, selectedSubStackIds, selectedJobTitleIds]);

  function getSkillLevel(categoryName, minervaJobCode) {
    if (!matrixData) return "";
    return matrixData.skillMap.get(`${categoryName}|${minervaJobCode}`) || "";
  }

  // --- ported verbatim from getSkillCellClass() ---
  function getSkillCellClass(categoryName, minervaJobCode) {
    const level = getSkillLevel(categoryName, minervaJobCode);
    if (!level) return "";
    const job = matrixData?.jobTitles.find((j) => j.minervaJobCode === minervaJobCode);
    const jobLevel = job?.minervaJobLevel || 1;
    let seniorityClass = "junior";
    if (jobLevel === 2) seniorityClass = "middle";
    else if (jobLevel >= 3) seniorityClass = "senior";

    const levelLower = level.toLowerCase();
    let skillClass = "has-value";
    if (levelLower.includes("n2h") || levelLower === "nice to have") skillClass = "n2h";
    else if (levelLower.includes("low") || levelLower === "beginner" || levelLower.includes("beg")) skillClass = "low";
    else if (levelLower.includes("avg") || levelLower === "average") skillClass = "avg";
    else if (levelLower.includes("pro") || levelLower === "proficient") skillClass = "pro";

    return `smx-skill-${seniorityClass}-${skillClass}`;
  }

  const hasAllSelections = selectedVerticalStackIds.length > 0 && selectedSubStackIds.length > 0 && selectedJobTitleIds.length > 0;

  return (
    <div className="smx-wrap" data-xls-filename="Skills-Matrix-Comparison" data-xls-sheet="Skills Matrix">
      <div className="smx-selector-row">
        <MultiSelectDropdown options={verticalStacks} model={selectedVerticalStackIds} title="Core Vertical Stack: " maxSelections={3} onChange={handleVerticalStackChange} />
        {availableSubStacks.length > 0 && (
          <>
            <span className="smx-sep">»</span>
            <MultiSelectDropdown options={availableSubStacks} model={selectedSubStackIds} title="Sub Stack: " maxSelections={0} onChange={handleSubStackChange} />
          </>
        )}
        {availableJobTitles.length > 0 && (
          <>
            <span className="smx-sep">»</span>
            <MultiSelectDropdown options={availableJobTitles} model={selectedJobTitleIds} title="Job Title: " maxSelections={5} onChange={setSelectedJobTitleIds} />
          </>
        )}
      </div>

      <div className={`smx-panel${loading ? " smx-loading" : ""}`}>
        <div className="smx-panel-head">
          <h3 className="smx-panel-title">
            Skill Set Matrix
            <button type="button" className="smx-info-btn" onClick={() => setShowLegend(true)} title="Show Color Legend">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" /><line x1="12" y1="17" x2="12" y2="17" /></svg>
            </button>
          </h3>
        </div>

        {matrixData && matrixData.jobTitles.length > 0 && hasAllSelections ? (
          <div className="smx-table-wrap">
            <table className="smx-table" data-xls-title="Skill Set Matrix">
              <thead>
                <tr>
                  <th colSpan={2} className="smx-fixed-head">Minerva Job Code</th>
                  {matrixData.jobTitles.map((job, i) => (
                    <th key={job.minervaJobCode} className={i % 2 === 0 ? "smx-alt-a" : "smx-alt-b"}>{job.minervaJobCode}</th>
                  ))}
                </tr>
                <tr>
                  <th colSpan={2} className="smx-fixed-head">Role</th>
                  {matrixData.jobTitles.map((job, i) => (
                    <th key={job.minervaJobCode} className={`smx-level-${job.minervaJobLevel >= 3 ? 3 : job.minervaJobLevel} ${i % 2 === 0 ? "smx-alt-b" : "smx-alt-a"}`}>{job.jobTitleName}</th>
                  ))}
                </tr>
                <tr>
                  <th colSpan={2} className="smx-fixed-head">Role Level</th>
                  {matrixData.jobTitles.map((job, i) => (
                    <th key={job.minervaJobCode} className={i % 2 === 0 ? "smx-alt-a" : "smx-alt-b"}>{job.minervaJobLevel}</th>
                  ))}
                </tr>
                <tr>
                  <th colSpan={2} className="smx-fixed-head">Years of Experience</th>
                  {matrixData.jobTitles.map((job, i) => (
                    <td key={job.minervaJobCode} className={i % 2 === 0 ? "smx-alt-b" : "smx-alt-a"}>{job.yearOnExperience}</td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixData.categories.map((category) => (
                  <Fragment key={`cat-${category.categoryType}`}>
                    <tr>
                      <th colSpan={2} className="smx-category-head">{category.categoryType}</th>
                      <th colSpan={matrixData.jobTitles.length} className="smx-category-head" />
                    </tr>
                    {category.skills.map((skill, skillIndex) => (
                      <tr key={`${category.categoryType}-${skill.categoryName}`}>
                        <td className={`smx-fixed-col-1 ${skillIndex % 2 === 0 ? "smx-alt-c" : "smx-alt-a"}`}>{skill.categoryName}</td>
                        <td className={`smx-fixed-col-2 ${skillIndex % 2 === 0 ? "smx-alt-a" : "smx-alt-c"}`}>{skill.categoryDescription}</td>
                        {matrixData.jobTitles.map((job, jobIndex) => {
                          const cellClass = getSkillCellClass(skill.categoryName, job.minervaJobCode);
                          return (
                            <td key={job.minervaJobCode} className={cellClass || (jobIndex % 2 === 0 ? "smx-alt-a" : "smx-alt-b")}>
                              {getSkillLevel(skill.categoryName, job.minervaJobCode)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className="smx-empty">
              <div className="smx-empty-text">
                {selectedVerticalStackIds.length === 0 && "Please select vertical stacks to begin."}
                {selectedVerticalStackIds.length > 0 && selectedSubStackIds.length === 0 && "Please select substacks to continue."}
                {selectedSubStackIds.length > 0 && selectedJobTitleIds.length === 0 && "Please select job titles to view the skill matrix."}
                {hasAllSelections && "No skill matrix data available for the selected combination."}
              </div>
            </div>
          )
        )}
      </div>

      {showLegend && (
        <div className="smx-modal-backdrop" onClick={() => setShowLegend(false)}>
          <div className="smx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="smx-modal-head">
              <h4>Skill Set Matrix - Color Legend</h4>
              <button type="button" className="smx-modal-close" onClick={() => setShowLegend(false)}>×</button>
            </div>
            <table className="smx-legend-table">
              <thead>
                <tr>
                  <th>Seniority</th>
                  <th className="smx-legend-junior">Apprentice/Junior</th>
                  <th className="smx-legend-middle">Middle</th>
                  <th className="smx-legend-senior">Senior/Lead</th>
                </tr>
              </thead>
              <tbody>
                <tr><td rowSpan={4}>Skill Level</td><td className="smx-skill-junior-n2h">N2H</td><td className="smx-skill-middle-n2h">N2H</td><td className="smx-skill-senior-n2h">N2H</td></tr>
                <tr><td className="smx-skill-junior-low">Low</td><td className="smx-skill-middle-low">Low</td><td className="smx-skill-senior-low">Low</td></tr>
                <tr><td className="smx-skill-junior-avg">Avg</td><td className="smx-skill-middle-avg">Avg</td><td className="smx-skill-senior-avg">Avg</td></tr>
                <tr><td className="smx-skill-junior-pro">Pro</td><td className="smx-skill-middle-pro">Pro</td><td className="smx-skill-senior-pro">Pro</td></tr>
              </tbody>
            </table>
            <div className="smx-legend-desc">
              <p><strong>Legend Explanation:</strong></p>
              <ul>
                <li><strong>N2H:</strong> Nice to Have - Optional skill for the role</li>
                <li><strong>Low:</strong> Basic understanding required</li>
                <li><strong>Avg:</strong> Average proficiency expected</li>
                <li><strong>Pro:</strong> Professional/Expert level required</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
